import { audio, type AudioCue } from "../../audio/audio";
import { btnHtml, bump, fmtCompact, ribbonHtml, tickNumber } from "../kit";
import { rewardFrom } from "../rewardFly/link";
import { gemIcon, goldIcon, marksIcon } from "./econArt";
import { SHOP_SKIN } from "../shopSkin";

/**
 * Confirmations and receipts for everything the Shop and the Market sell (FINISH_PLAN WP5): one confirm dialog in the
 * kit's style (the parchment dialog with the ribbon title, as ui/dialog.ts) that names what is bought and what it
 * costs, and the receipt — the currency pill ticks down, what was bought flies to where it lands, a toast says it.
 * Presentation only: the purchase itself is the caller's (@highmarch/app Economy / PurchaseService).
 */

export type Currency = "gems" | "marks" | "gold";

/**
 * The cues the economy plays, in one place, per docs/AUDIO.md "Meta cues": `buy` is any gem spend (a gem pack, the
 * builder, speed-ups, resources, skins, the Black Market), `crate` a Supply Crate, `trade`/`sell` the Trading Post,
 * `queue` "Queue next", `event` an event reward claimed or a milestone reached. The Tribute keeps the chest cue (it is one).
 */
export const ECON_CUES = {
  buy: "purchase", crate: "crateOpen", trade: "marketBuy", sell: "marketSell", queue: "queueSet", event: "eventProgress", tribute: "chest", tap: "ui",
} as const satisfies Record<string, AudioCue>;
export function econCue(k: keyof typeof ECON_CUES): void {
  try { audio.play(ECON_CUES[k]); } catch { /* no audio in tests */ }
}

export interface ConfirmOptions {
  readonly title: string;
  /** Markup for the dialog's middle: what is bought (the caller's HTML, built from game data only). */
  readonly body: string;
  readonly currency: Currency | null;
  /** The price, or a label (a store's "$1.99"). */
  readonly price: number | string;
  readonly confirm?: string;
  /** Extra class on the dialog (`bm` for the Black Market's dark dressing). */
  readonly className?: string;
  /** Opened from a shop screen: the dialog wears the shop's premium skin (ui/shopSkin.ts). */
  readonly shop?: boolean;
}

/** A purchase confirm: the ribbon title, what it is, and "BUY · 💎 40". Resolves true on BUY; the scrim or CANCEL is no. */
export function confirmPurchase(parent: HTMLElement, o: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const scrim = document.createElement("div");
    scrim.className = `scrim econconfirm${o.shop ? ` ${SHOP_SKIN}` : ""}`;
    const icon = o.currency === "gems" ? gemIcon(20) : o.currency === "marks" ? marksIcon(20) : o.currency === "gold" ? goldIcon(20) : "";
    const priceLabel = typeof o.price === "number" ? fmtCompact(o.price) : o.price;
    scrim.innerHTML = `<div class="panel dialog econdlg ${o.className ?? ""}">${ribbonHtml(o.title, "", "dtitle disp")}<div class="ebody">${o.body}</div>
      <div class="row two">${btnHtml("CANCEL", "dark", { className: "cancel disp" })}<button class="btn gold ok disp accent"><span class="lbl"><span class="disp">${o.confirm ?? "BUY"}</span></span><span class="eprice">${icon}<b>${priceLabel}</b></span></button></div></div>`;
    const done = (v: boolean) => { scrim.remove(); resolve(v); };
    scrim.querySelector<HTMLButtonElement>(".cancel")!.onclick = () => done(false);
    scrim.querySelector<HTMLButtonElement>(".ok")!.onclick = () => done(true);
    scrim.onclick = (e) => { if (e.target === scrim) done(false); };
    parent.append(scrim);
  });
}

/** The hub's pill for a currency (features' pills sit in `.fpill[data-pill]`), or null off the hub. */
export function currencyPill(currency: Currency): HTMLElement | null {
  return document.querySelector<HTMLElement>(`.hub .fpill[data-pill="${currency}"] :is(.pill, .storebtn)`);
}

/** The pill rolls from what it showed to `to` (after the hub re-rendered it with the new balance) and pops. */
export function tickPill(currency: Currency, from: number, to: number): void {
  const pill = currencyPill(currency);
  // the number: a kit pill's <span>, the gems Store button's <b> (features/economy.ts storeButtonHtml)
  const span = pill?.querySelector<HTMLElement>(":scope > :is(span:not(.plus), b)");
  if (!span || from === to) return;
  tickNumber(span, from, to, 500, fmtCompact);
  bump(pill, 1.14, 200);
}

/**
 * Currency bought or won from `from` (a gem pack, a VIP bundle): the reward flight (ui/rewardFly) flies it from there
 * into its counter and ticks it up, as it does every grant. `before`/`after` stay for the callers; the flight reads the
 * save itself.
 */
export function flyCurrency(from: Element | null, _currency: Currency, _before: number, _after: number): void {
  rewardFrom(from);   // reward flight hook (ui/rewardFly/link.ts)
}

/** A card pops and glows as it is bought (the crate "opens", an offer is stamped SOLD). */
export function purchasePop(card: Element | null): void {
  if (!card) return;
  card.classList.remove("justbought");
  void (card as HTMLElement).offsetWidth;          // restart the CSS reveal on a second purchase of the same card
  card.classList.add("justbought");
}
